import { createContext, memo, ReactNode, useContext, useMemo } from "react";

import { type Location, type Service, type User, type WorkingHours } from "~/api/graphql-types";
import { useGetResourcesQuery } from "~/api/scheduling";

export interface IResource {
  id: number;
  name: string;
  workingHours: WorkingHours[];
  type: "worker" | "space";
  services: Service[];
}

export interface IResourcesContext {
  isLoading: boolean;
  isError: boolean;
  loadedOnce: boolean;
  workers: IResource[];
  spaces: IResource[];
}

const workerToResource = (w: User): IResource => {
  return {
    id: w.id,
    name: w.name ?? "",
    workingHours: (w.workingHours ?? []) as WorkingHours[],
    type: "worker",
    services: (w.services ?? []) as Service[],
  };
};

const spaceToResource = (s: Location): IResource => {
  return {
    id: s.id,
    name: s.name ?? "",
    workingHours: [],
    type: "space",
    services: (s.services ?? []) as Service[],
  };
};

const ResourcesContext = createContext<IResourcesContext>({
  isLoading: false,
  isError: false,
  loadedOnce: true,
  workers: [],
  spaces: [],
});
export const useResources = () => useContext(ResourcesContext);

export const ResourcesState = memo(({ children }: { children: ReactNode }) => {
  const { data, isError, isLoading } = useGetResourcesQuery();
  const sortedWorkers = (data?.workers?.results ?? []).sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );
  const sortedSpaces = (data?.spaces ?? []).sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );
  const value = useMemo(
    () => ({
      isLoading,
      isError,
      loadedOnce: !!data,
      workers: data ? sortedWorkers.map(workerToResource) : [],
      spaces: data ? sortedSpaces.map(spaceToResource) : [],
    }),
    [isLoading, isError, data]
  );
  return <ResourcesContext.Provider value={value}>{children}</ResourcesContext.Provider>;
});
ResourcesState.displayName = "ResourcesState";
